import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import type { DeprecatedChannelAuthorizer, DeprecatedAuthorizerOptions } from 'pusher-js/types/src/core/auth/deprecated_channel_authorizer';
import type Channel from 'pusher-js/types/src/core/channels/channel';
import * as authService from './auth.service';
import type { ListingPullStatus } from '@/types/listing';

(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

let echoInstance: Echo<'reverb'> | null = null;

export const getEcho = (): Echo<'reverb'> => {
  if (!echoInstance) {
    echoInstance = new Echo({
      broadcaster: 'reverb',
      key: import.meta.env.VITE_REVERB_APP_KEY as string,
      wsHost: import.meta.env.VITE_REVERB_HOST as string,
      wsPort: Number(import.meta.env.VITE_REVERB_PORT),
      wssPort: Number(import.meta.env.VITE_REVERB_PORT),
      forceTLS: (import.meta.env.VITE_REVERB_SCHEME as string) === 'https',
      enabledTransports: ['ws', 'wss'],
      authorizer: (channel: Channel, _options: DeprecatedAuthorizerOptions): DeprecatedChannelAuthorizer => ({
        authorize: (socketId, callback) => {
          fetch(`${API_BASE}/broadcasting/auth`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authService.getToken() ?? ''}`,
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({ socket_id: socketId, channel_name: channel.name }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((data: Parameters<typeof callback>[1]) => callback(null, data))
            .catch((err: Error) => callback(err, null));
        },
      }),
    });
  }
  return echoInstance;
};

export const connect = (userId: number, orgId: number): void => {
  const echo = getEcho();
  echo.private(`user.${userId}`);
  echo.private(`organization.${orgId}`);
};

export const disconnect = (): void => {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
};

interface BroadcastNotification {
  type: string;
  title: string;
  body: string;
  action_url?: string;
  category?: string;
  priority?: string;
  metadata?: Record<string, unknown>;
}

export const subscribeToNotifications = (
  userId: number,
  orgId: number,
  callback: (notification: BroadcastNotification) => void
): void => {
  const echo = getEcho();
  echo
    .private(`user.${userId}`)
    .listen('.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', callback);
  echo
    .private(`organization.${orgId}`)
    .listen('.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', callback);
};

interface LandscapeStatusEvent {
  landscape_id: number;
  status: string;
  status_info: string;
}

export const subscribeToLandscapeUpdates = (
  orgId: number,
  callback: (event: LandscapeStatusEvent) => void
): void => {
  const echo = getEcho();
  echo
    .private(`organization.${orgId}`)
    .listen('.landscape.status.updated', callback);
};

export const unsubscribeFromLandscapeUpdates = (orgId: number): void => {
  const echo = getEcho();
  echo.private(`organization.${orgId}`).stopListening('.landscape.status.updated');
};

interface KeywordBankEnrichedEvent {
  product_id: number;
}

export const subscribeToKeywordBankEnrichment = (
  orgId: number,
  callback: (event: KeywordBankEnrichedEvent) => void
): void => {
  const echo = getEcho();
  echo
    .private(`organization.${orgId}`)
    .listen('.keyword-bank.enriched', callback);
};

export const unsubscribeFromKeywordBankEnrichment = (orgId: number): void => {
  const echo = getEcho();
  echo.private(`organization.${orgId}`).stopListening('.keyword-bank.enriched');
};

interface LandscapeProductsUpdatedEvent {
  landscape_id: number;
}

export const subscribeToLandscapeProductsUpdates = (
  orgId: number,
  callback: (event: LandscapeProductsUpdatedEvent) => void
): void => {
  const echo = getEcho();
  echo
    .private(`organization.${orgId}`)
    .listen('.landscape.products.updated', callback);
};

export const unsubscribeFromLandscapeProductsUpdates = (orgId: number): void => {
  const echo = getEcho();
  echo.private(`organization.${orgId}`).stopListening('.landscape.products.updated');
};

export interface ScorecardStatusEvent {
  scorecard_id: number;
  asin: string;
  status: string;
  step?: string;
}

export const subscribeToScorecardUpdates = (
  orgId: number,
  callback: (event: ScorecardStatusEvent) => void
): void => {
  const echo = getEcho();
  echo
    .private(`organization.${orgId}`)
    .listen('.scorecard.status.updated', callback);
};

export const unsubscribeFromScorecardUpdates = (orgId: number): void => {
  const echo = getEcho();
  echo.private(`organization.${orgId}`).stopListening('.scorecard.status.updated');
};

/**
 * `ListingPullStatusUpdated` (`data-flow.md § Flow: listing content pull`,
 * hop 8). `broadcastAs()` returns the dotless `listing.status.updated`; the
 * leading dot below is Echo's `.listen()` convention for a custom event
 * name, matching every other subscription in this file. No `cooldown_until`
 * -- the status column is the contract, not the event.
 *
 * `triggered_by_user_id` is optional, not required: an old backend sends no
 * key at all, and `listing.events.test.ts`'s `event()` helper builds events
 * without it. Required would fail `tsc -p tsconfig.app.json`, which the
 * quality ratchet runs (`decisions/a-listing-toast-is-addressed-by-user-and-tab-mark.md
 * § The event gains triggered_by_user_id, read from the row`).
 */
export interface ListingStatusEvent {
  product_id: number;
  asin: string;
  status: ListingPullStatus;
  is_first_pull: boolean;
  triggered_by_user_id?: number | null;
}

export const subscribeToListingUpdates = (
  orgId: number,
  callback: (event: ListingStatusEvent) => void
): void => {
  const echo = getEcho();
  echo
    .private(`organization.${orgId}`)
    .listen('.listing.status.updated', callback);
};

export const unsubscribeFromListingUpdates = (orgId: number): void => {
  const echo = getEcho();
  echo.private(`organization.${orgId}`).stopListening('.listing.status.updated');
};
